import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { toast } from 'sonner';
import { CalendarBlank, FilmStrip, Camera, Sparkle, ArrowRight, Tag, CalendarPlus } from '@phosphor-icons/react';
import { EventDropNav } from '../components/EventDropLogo';
import StudioPassBanner from '../components/StudioPassBanner';
import PricingTab from '../components/PricingTab';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Multi-event dashboard for repeat hosts.
 * - Lists every event belonging to the signed-in user
 * - Shows a repeat-host discount (REPEAT20 for 20% off) when they already have 1+ paid event
 * - One-tap revisit into each event
 * Retention play: brides who book weddings also end up booking baby showers, work parties, etc.
 */
const MyEvents = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [discountCode, setDiscountCode] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(() => searchParams.get('tab') === 'pricing' ? 'pricing' : 'events');

  // Celebrate a successful purchase return. /thank-you can deep-link back here
  // with ?purchased=true (or ?purchased=<productId>) to trigger confetti and a
  // "Create your next event" toast.
  useEffect(() => {
    const purchased = searchParams.get('purchased');
    if (!purchased) return;
    // Staggered confetti burst
    const colors = ['#C29D52', '#B89762', '#FFCC00', '#FF6B6B', '#32FDC8'];
    const fire = (opts) => confetti({ ...opts, colors, disableForReducedMotion: true });
    fire({ particleCount: 80, spread: 70, origin: { y: 0.6 } });
    setTimeout(() => {
      fire({ particleCount: 60, angle: 60, spread: 55, origin: { x: 0, y: 0.7 } });
      fire({ particleCount: 60, angle: 120, spread: 55, origin: { x: 1, y: 0.7 } });
    }, 220);
    toast.success('Payment confirmed — ready to create your next event?', {
      duration: 6000,
      action: { label: 'Create event', onClick: () => navigate('/pricing') },
    });
    // Clear the param so refresh doesn't re-fire confetti
    const next = new URLSearchParams(searchParams);
    next.delete('purchased');
    next.delete('product');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('session_token');
    if (!token) { navigate('/login'); return; }
    axios.get(`${API}/users/me/events`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        setEvents(r.data?.events || []);
        setDiscountCode(r.data?.repeat_discount_code || null);
      })
      .catch(err => {
        if (err.response?.status === 401) navigate('/login');
        else setError('Unable to load your events.');
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const switchTab = (next) => {
    setTab(next);
    const params = new URLSearchParams(searchParams);
    if (next === 'pricing') params.set('tab', 'pricing'); else params.delete('tab');
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0F1014] text-white" data-testid="my-events-page">
      <EventDropNav />
      <main className="max-w-4xl mx-auto px-5 py-10">
        <h1 className="text-3xl sm:text-4xl font-black mb-2">Your Events</h1>
        <p className="text-sm text-gray-400 mb-6">All the moments you've captured — revisit, re-share, or start something new.</p>

        {/* Tab switcher */}
        <div
          className="inline-flex p-1 rounded-2xl bg-white/5 border border-white/10 mb-6"
          role="tablist"
          data-testid="my-events-tabs"
        >
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'events'}
            onClick={() => switchTab('events')}
            className={`px-4 h-9 rounded-xl text-sm font-semibold transition-colors inline-flex items-center gap-2 ${
              tab === 'events' ? 'bg-white text-[#0F1014]' : 'text-white/60 hover:text-white'
            }`}
            data-testid="tab-events"
          >
            <CalendarBlank size={14} weight="bold" /> My Events
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'pricing'}
            onClick={() => switchTab('pricing')}
            className={`px-4 h-9 rounded-xl text-sm font-semibold transition-colors inline-flex items-center gap-2 ${
              tab === 'pricing' ? 'bg-white text-[#0F1014]' : 'text-white/60 hover:text-white'
            }`}
            data-testid="tab-pricing"
          >
            <Tag size={14} weight="bold" /> Pricing
          </button>
        </div>

        {tab === 'pricing' ? (
          <>
            <PricingTab />
            {events.length > 0 && (
              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={() => switchTab('events')}
                  className="inline-flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-semibold text-white/70 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10"
                  data-testid="back-to-events"
                >
                  <CalendarPlus size={14} weight="bold" /> Back to your events
                </button>
              </div>
            )}
          </>
        ) : (
          <>
        <StudioPassBanner events={events} />

        {discountCode && (
          <div className="mb-8 p-5 rounded-2xl border relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(255,107,107,0.12), rgba(255,204,0,0.08))', borderColor: 'rgba(255,204,0,0.25)' }} data-testid="repeat-discount-banner">
            <div className="flex items-center gap-4">
              <Sparkle size={28} weight="bold" style={{ color: '#FFCC00' }} />
              <div className="flex-1">
                <p className="text-xs font-black uppercase tracking-wider text-white/50 mb-0.5">Welcome back</p>
                <p className="font-bold text-lg">20% off your next event</p>
                <p className="text-sm text-gray-400 mt-0.5">Use code <span className="font-mono font-bold text-yellow-300" data-testid="repeat-code">{discountCode}</span> at checkout.</p>
              </div>
              <button onClick={() => navigate('/pricing')} className="hidden sm:flex items-center gap-1.5 px-4 py-2.5 rounded-full font-bold text-sm text-white" style={{ background: 'linear-gradient(135deg, #FF6B6B, #FFCC00)' }}>
                Plan new event <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="py-20 text-center text-gray-500">Loading…</div>
        ) : error ? (
          <div className="py-20 text-center text-red-400">{error}</div>
        ) : events.length === 0 ? (
          <div className="py-20 text-center border border-white/10 rounded-2xl" data-testid="empty-state">
            <p className="text-gray-400 mb-4">You haven't created an event yet.</p>
            <button onClick={() => navigate('/pricing')} className="px-6 py-3 rounded-full font-bold bg-white text-black hover:bg-white/90">Create your first event</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3" data-testid="events-grid">
            {events.map(ev => {
              const filmReady = ev.film_status === 'ready';
              const photoCount = ev.photo_count || 0;
              return (
                <div key={ev.event_id}
                  onClick={() => navigate(`/event/${ev.slug}`)}
                  className="group relative rounded-2xl p-5 border border-white/10 bg-[#1B1C22] hover:border-white/25 transition-all cursor-pointer"
                  data-testid={`event-card-${ev.slug}`}>
                  <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: filmReady ? 'linear-gradient(135deg, #32FDC8, #00E0A1)' : 'rgba(255,255,255,0.06)' }}>
                      <FilmStrip size={22} weight="bold" style={{ color: filmReady ? '#0F1014' : '#A0A0A0' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-bold text-base sm:text-lg truncate min-w-0 flex-1">{ev.event_name || ev.slug}</h3>
                        {ev.has_pro_upgrade && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider flex-shrink-0" style={{ background: 'linear-gradient(135deg, #FF6B6B, #FFCC00)', color: '#0F1014' }}>Pro</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-400 flex-wrap">
                        {ev.event_date && <span className="flex items-center gap-1"><CalendarBlank size={12} weight="bold" />{new Date(ev.event_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                        <span className="flex items-center gap-1"><Camera size={12} weight="bold" />{photoCount} moments</span>
                        <span className="capitalize" style={{ color: filmReady ? '#32FDC8' : '#A0A0A0' }}>Film: {ev.film_status || 'none'}</span>
                      </div>
                    </div>
                    <ArrowRight size={18} weight="bold" className="text-white/30 group-hover:text-white/70 transition-colors flex-shrink-0 mt-1" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
          </>
        )}
      </main>
    </div>
  );
};

export default MyEvents;
